/**
 * Org-level MFA grace-period derivation (#1887) — pure, no React.
 *
 * The backend is the source of truth for WHEN a member's grace period ends
 * (`public.org_mfa_deadline(...)`, shared by the JWT hook and the members-list
 * query). This module only classifies an already-computed deadline against
 * "now" for rendering, so the badge on the members tab and the banner a
 * member sees are the same classification of the same instant.
 *
 * Boundary: enforcement starts AT the deadline instant — `now >= deadline` is
 * `overdue`, mirroring the hook's `now() >= deadline`.
 */

type OrgMemberMfaStatus =
  /** Org does not require MFA — nothing to show. */
  | 'not_required'
  /** Member has a verified factor; the org's requirement is satisfied. */
  | 'enabled'
  /** Required, not enrolled, deadline still ahead. */
  | 'grace'
  /** Required, not enrolled, deadline reached — AAL1 sessions are blocked. */
  | 'overdue';

interface OrgMemberMfaInput {
  /** Org admin's opt-in. */
  requireMfa: boolean;
  /** Member has a VERIFIED factor. */
  mfaEnabled: boolean;
  /** ISO instant the member's grace ends; null when not required. */
  mfaDeadline: string | null;
}

const MS_PER_DAY = 86_400_000;

export function deriveOrgMemberMfaStatus(
  input: OrgMemberMfaInput,
  now: Date = new Date()
): OrgMemberMfaStatus {
  if (!input.requireMfa) return 'not_required';
  if (input.mfaEnabled) return 'enabled';
  const deadline = parseDeadline(input.mfaDeadline);
  // A required org with no computable deadline is treated as enforced now:
  // the hook's `org_require_mfa` would be false in that state (NULL deadline
  // short-circuits it), but a NULL here means a data gap, and the honest
  // rendering of a gap on an access-control surface is the stricter one.
  if (deadline === null) return 'overdue';
  return now.getTime() >= deadline.getTime() ? 'overdue' : 'grace';
}

/**
 * Whole days until `deadline`, rounded UP, floored at 0 — "3 days left"
 * for anything between 48 h and 72 h. Null when no deadline.
 */
export function daysUntilDeadline(
  mfaDeadline: string | null,
  now: Date = new Date()
): number | null {
  const deadline = parseDeadline(mfaDeadline);
  if (deadline === null) return null;
  const diff = deadline.getTime() - now.getTime();
  if (diff <= 0) return 0;
  return Math.ceil(diff / MS_PER_DAY);
}

function parseDeadline(iso: string | null): Date | null {
  if (!iso) return null;
  const parsed = new Date(iso);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

/** Grace periods offered in the Security tab's select (#1887). */
export const MFA_GRACE_OPTIONS: readonly string[] = [
  '0',
  '3',
  '7',
  '14',
  '30',
  '90',
];

/**
 * The select's options, guaranteed to include `current`.
 *
 * Any 0..90 value is legal server-side, so an org configured through the API
 * with e.g. `5` would otherwise render an empty trigger — the admin could not
 * see the period in force. The current value is merged in and the list kept
 * in ascending numeric order.
 */
export function graceSelectOptions(current: string): string[] {
  if (MFA_GRACE_OPTIONS.includes(current)) return [...MFA_GRACE_OPTIONS];
  return [...MFA_GRACE_OPTIONS, current].sort((a, b) => Number(a) - Number(b));
}
